import React, { useState, useEffect } from "react";
import axios from "axios";
import { Boxes, Plus, Trash2, X, Pencil, PlayCircle, TrendingDown, Info, PackageX, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const KATEGORI_LIST = ["Perangkat Jaringan", "Kendaraan", "Alat", "Lainnya"];
const EMPTY = {
  nama: "", kategori: "Perangkat Jaringan", tanggal_beli: new Date().toISOString().slice(0, 10), harga_beli: 0,
  umur_tahun: 4, metode: "garis_lurus", sumber_dana_akun_id: "", catatan: "", status: "aktif",
};

const AsetForm = ({ initial, akunList, onClose, onSuccess }) => {
  const kasBank = (akunList || []).filter((a) => a.tipe === "tunai" || a.tipe === "bank");
  const [form, setForm] = useState(initial ? { ...initial } : { ...EMPTY, sumber_dana_akun_id: kasBank[0]?.id || "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const perBulan = form.umur_tahun > 0 ? Number(form.harga_beli) / (Number(form.umur_tahun) * 12) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, harga_beli: parseFloat(form.harga_beli) || 0, umur_tahun: parseFloat(form.umur_tahun) || 0 };
      if (initial?.id) await axios.put(`${BACKEND_URL}/api/aset/${initial.id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/aset`, payload, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal simpan aset"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-[#0e1628]">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><Boxes className="w-4 h-4 text-cyan-400" />{initial?.id ? "Edit Aset" : "Tambah Aset Tetap"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Aset *</label>
              <input required value={form.nama} onChange={(e) => setField("nama", e.target.value)} placeholder="cth: Perangkat OLT Huawei" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="aset-nama-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Kategori</label>
              <select value={form.kategori} onChange={(e) => setField("kategori", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="aset-kategori-select">
                {KATEGORI_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Tanggal Pembelian *</label>
              <input required type="date" value={form.tanggal_beli} onChange={(e) => setField("tanggal_beli", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="aset-tanggal-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Harga Beli (Rp) *</label>
              <input required type="number" min={0} value={form.harga_beli} onChange={(e) => setField("harga_beli", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="aset-harga-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Umur Ekonomis (Tahun) *</label>
              <input required type="number" min={0.5} step="0.5" value={form.umur_tahun} onChange={(e) => setField("umur_tahun", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="aset-umur-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Metode Penyusutan</label>
              <select value={form.metode} onChange={(e) => setField("metode", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="aset-metode-select">
                <option value="garis_lurus">Garis Lurus (Straight-Line)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Sumber Dana Pembelian (Kas/Bank) *</label>
            <select required value={form.sumber_dana_akun_id} onChange={(e) => setField("sumber_dana_akun_id", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="aset-sumber-select">
              <option value="">-- Pilih Akun --</option>
              {kasBank.map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
            </select>
            {!initial?.id && <p className="text-[10px] text-amber-400/80 mt-1">Saldo akun ini otomatis berkurang senilai harga beli saat aset disimpan.</p>}
          </div>
          <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-[11px] text-cyan-200 flex items-center gap-2">
            <Info className="w-4 h-4 shrink-0" /> Penyusutan/bulan (garis lurus): <b className="font-mono">{rp(perBulan)}</b>
          </div>
          <div className="pt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
            <button type="submit" disabled={loading} data-testid="submit-aset-btn" className="px-4 py-1.5 bg-cyan-600 text-white rounded-xl text-xs font-bold disabled:opacity-50">Simpan</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DisposeForm = ({ aset, akunList, onClose, onSuccess }) => {
  const kasBank = (akunList || []).filter((a) => a.tipe === "tunai" || a.tipe === "bank");
  const [form, setForm] = useState({ tanggal_lepas: new Date().toISOString().slice(0, 10), harga_jual: 0, akun_terima_id: kasBank[0]?.id || "", catatan: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const labaRugi = (parseFloat(form.harga_jual) || 0) - (aset.nilai_buku || 0);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/aset/${aset.id}/lepas`, { ...form, harga_jual: parseFloat(form.harga_jual) || 0 }, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal melepas aset"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><PackageX className="w-4 h-4 text-orange-400" /> Lepas / Jual Aset</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs">{error}</div>}
          <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-700 text-xs text-slate-300">
            <p className="font-semibold text-white">{aset.nama}</p>
            <p>Nilai Buku saat ini: <b className="font-mono text-cyan-300">{rp(aset.nilai_buku)}</b></p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Tanggal Lepas</label>
              <input type="date" value={form.tanggal_lepas} onChange={(e) => setForm({ ...form, tanggal_lepas: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="lepas-tanggal-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Harga Jual (Rp)</label>
              <input type="number" min={0} value={form.harga_jual} onChange={(e) => setForm({ ...form, harga_jual: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="lepas-harga-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Uang Masuk ke Akun</label>
            <select value={form.akun_terima_id} onChange={(e) => setForm({ ...form, akun_terima_id: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="lepas-akun-select">
              <option value="">-- Tanpa penerimaan kas --</option>
              {kasBank.map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
            </select>
          </div>
          <div className={`p-2.5 rounded-xl border text-xs flex items-center justify-between ${labaRugi >= 0 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`}>
            <span>{labaRugi >= 0 ? "Laba Pelepasan" : "Rugi Pelepasan"}</span>
            <b className="font-mono" data-testid="lepas-labarugi">{rp(labaRugi)}</b>
          </div>
          <p className="text-[10px] text-slate-500">Penyusutan aset ini akan dihentikan. Laba/rugi tercatat ke ekuitas (Laporan Perubahan Modal & Neraca).</p>
          <div className="pt-1 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
            <button type="submit" disabled={loading} data-testid="submit-lepas-btn" className="px-4 py-1.5 bg-orange-600 text-white rounded-xl text-xs font-bold disabled:opacity-50">Konfirmasi Lepas</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const printKIB = (a, brand) => {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const perus = brand.perusahaan || {};
  const namaPerus = perus.nama_isp || brand.app_name || "PERUSAHAAN";
  const logo = brand.app_logo || "";
  const rows = (a.riwayat || []).map((r, i) => `<tr><td class="c">${i + 1}</td><td class="c">${esc(r.periode)}</td><td class="c">${esc(r.tanggal)}</td><td class="r">${rp(r.nominal)}</td></tr>`).join("");
  const totalPeny = (a.riwayat || []).reduce((s, r) => s + (r.nominal || 0), 0);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>KIB - ${esc(a.nama)}</title>
  <style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:24px}
  .head{display:flex;align-items:center;gap:14px;border-bottom:3px double #111;padding-bottom:10px}.head img{height:52px}.head h1{font-size:18px;margin:0}.head p{font-size:11px;margin:2px 0 0;color:#444}
  .title{text-align:center;margin:10px 0 12px}.title h2{font-size:15px;margin:0;text-transform:uppercase}
  table{width:100%;border-collapse:collapse;font-size:11.5px;margin:6px 0}th,td{border:1px solid #333;padding:5px 8px}th{background:#eee;text-align:left}
  td.c{text-align:center}td.r{text-align:right;font-family:'Courier New',monospace}
  .info td{border:none;padding:3px 6px}.info td.k{color:#555;width:170px}
  tr.total td{background:#f4f4f4;font-weight:bold}
  .foot{margin-top:26px;font-size:11px;display:flex;justify-content:flex-end}.sign{text-align:center;width:220px}.sign .line{margin-top:56px;border-top:1px solid #111;padding-top:4px}
  @media print{body{margin:12mm}}</style></head><body>
  <div class="head">${logo ? `<img src="${esc(logo)}"/>` : ""}<div><h1>${esc(namaPerus)}</h1>${perus.alamat ? `<p>${esc(perus.alamat)}</p>` : ""}</div></div>
  <div class="title"><h2>Kartu Inventaris Barang (KIB)</h2></div>
  <table class="info">
    <tr><td class="k">Nama Aset</td><td>: <b>${esc(a.nama)}</b></td><td class="k">Kategori</td><td>: ${esc(a.kategori || "-")}</td></tr>
    <tr><td class="k">Tanggal Pembelian</td><td>: ${esc(a.tanggal_beli)}</td><td class="k">Umur Ekonomis</td><td>: ${esc(a.umur_tahun)} tahun</td></tr>
    <tr><td class="k">Harga Perolehan</td><td>: ${rp(a.harga_beli)}</td><td class="k">Penyusutan/Bulan</td><td>: ${rp(a.penyusutan_per_bulan)}</td></tr>
    <tr><td class="k">Akumulasi Penyusutan</td><td>: ${rp(a.akumulasi_penyusutan)}</td><td class="k">Nilai Buku</td><td>: <b>${rp(a.nilai_buku)}</b></td></tr>
    <tr><td class="k">Metode</td><td>: Garis Lurus</td><td class="k">Status</td><td>: ${a.status === "dilepas" ? "DILEPAS (" + esc(a.tanggal_lepas || "") + ")" : "Aktif"}</td></tr>
  </table>
  <h3 style="font-size:12px;margin:14px 0 4px;border-left:4px solid #111;padding-left:8px">Riwayat Penyusutan Bulanan</h3>
  <table><thead><tr><th class="c">No</th><th class="c">Periode</th><th class="c">Tanggal</th><th style="text-align:right">Nominal Penyusutan</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="4" class="c">Belum ada penyusutan tercatat</td></tr>'}
  <tr class="total"><td colspan="3" style="text-align:right">TOTAL PENYUSUTAN</td><td class="r">${rp(totalPeny)}</td></tr></tbody></table>
  <div class="foot"><div class="sign"><div>Penanggung Jawab Aset,</div><div class="line">( ............................... )</div></div></div>
  <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) { alert("Popup diblokir browser. Izinkan popup untuk mencetak."); return; }
  w.document.open(); w.document.write(html); w.document.close();
};

export const MasterAsetPage = () => {
  const [items, setItems] = useState([]);
  const [akunList, setAkunList] = useState([]);
  const [brand, setBrand] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [disposing, setDisposing] = useState(null);
  const [notif, setNotif] = useState("");
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [asetRes, kasRes, brandRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/aset`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/kas/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/branding`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setItems(asetRes.data || []);
      setAkunList(kasRes.data?.akun || []);
      setBrand(brandRes.data || {});
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus aset "${nama}"? Riwayat penyusutannya juga dihapus.`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/aset/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const jalankanPenyusutan = async () => {
    if (!window.confirm("Jalankan penyusutan untuk bulan berjalan sekarang? (aman diulang — sudah anti-dobel per bulan)")) return;
    setRunning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/aset/jalankan-penyusutan`, {}, { withCredentials: true });
      setNotif(res.data.message); load(); setTimeout(() => setNotif(""), 6000);
    } catch (err) { alert(err.response?.data?.detail || "Gagal menjalankan penyusutan"); } finally { setRunning(false); }
  };

  const cetakKIB = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/aset/${id}`, { withCredentials: true });
      printKIB(res.data, brand);
    } catch (err) { alert("Gagal memuat data aset untuk cetak"); }
  };

  const aktif = items.filter((a) => a.status !== "dilepas");
  const totalPerolehan = aktif.reduce((s, a) => s + (a.harga_beli || 0), 0);
  const totalAkum = aktif.reduce((s, a) => s + (a.akumulasi_penyusutan || 0), 0);
  const totalBuku = aktif.reduce((s, a) => s + (a.nilai_buku || 0), 0);

  // Rekap penyusutan per kategori (aset aktif)
  const perKategori = {};
  aktif.forEach((a) => {
    const k = a.kategori || "Lainnya";
    if (!perKategori[k]) perKategori[k] = { jumlah: 0, perolehan: 0, akum: 0, buku: 0, perBulan: 0 };
    perKategori[k].jumlah += 1;
    perKategori[k].perolehan += a.harga_beli || 0;
    perKategori[k].akum += a.akumulasi_penyusutan || 0;
    perKategori[k].buku += a.nilai_buku || 0;
    perKategori[k].perBulan += a.penyusutan_per_bulan || 0;
  });

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="aset-page">
      {notif && <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs" data-testid="aset-notif">{notif}</div>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-white flex items-center gap-2"><Boxes className="w-5 h-5 text-cyan-400" /> Manajemen Aset Tetap</h1>
          <p className="text-xs text-slate-400 mt-0.5">Inventaris & penyusutan alat ISP (garis lurus). Penyusutan otomatis tiap akhir bulan + transfer dana ke cadangan.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={jalankanPenyusutan} disabled={running} data-testid="run-penyusutan-btn" className="px-3.5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 disabled:opacity-50"><PlayCircle className="w-4 h-4" /> Jalankan Penyusutan Bulan Ini</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-aset-btn" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5"><Plus className="w-4 h-4" /> Tambah Aset</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400">Total Nilai Perolehan (aktif)</p><p className="text-xl font-black text-white font-mono mt-1">{rp(totalPerolehan)}</p></div>
        <div className="p-4 rounded-2xl bg-rose-950/30 border border-rose-500/40"><p className="text-xs text-slate-400">Akumulasi Penyusutan</p><p className="text-xl font-black text-rose-400 font-mono mt-1">{rp(totalAkum)}</p></div>
        <div className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/40"><p className="text-xs text-slate-400">Nilai Buku Saat Ini</p><p className="text-xl font-black text-cyan-400 font-mono mt-1">{rp(totalBuku)}</p></div>
      </div>

      <div className="rounded-2xl bg-[#0e1628] border border-slate-800 overflow-x-auto" data-testid="aset-per-kategori">
        <h3 className="text-sm font-black text-white px-3 py-2.5 border-b border-slate-800">Laporan Penyusutan per Kategori</h3>
        <table className="w-full text-left text-xs min-w-[620px]">
          <thead className="bg-slate-900/60 text-slate-400"><tr>
            <th className="py-2.5 px-3">Kategori</th><th className="py-2.5 px-3 text-center">Jml Aset</th>
            <th className="py-2.5 px-3 text-right">Perolehan</th><th className="py-2.5 px-3 text-right">Akumulasi</th>
            <th className="py-2.5 px-3 text-right">Nilai Buku</th><th className="py-2.5 px-3 text-right">Penyusutan/Bln</th>
          </tr></thead>
          <tbody>
            {Object.keys(perKategori).length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-500">-</td></tr>}
            {Object.entries(perKategori).map(([k, v]) => (
              <tr key={k} className="border-t border-slate-800/60" data-testid={`kategori-row-${k}`}>
                <td className="py-2.5 px-3 text-slate-200 font-semibold">{k}</td>
                <td className="py-2.5 px-3 text-center text-slate-300">{v.jumlah}</td>
                <td className="py-2.5 px-3 text-right font-mono text-slate-200">{rp(v.perolehan)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-rose-300">{rp(v.akum)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-cyan-300">{rp(v.buku)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-amber-300">{rp(v.perBulan)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl bg-[#0e1628] border border-slate-800 overflow-x-auto">
        <table className="w-full text-left text-xs min-w-[920px]">
          <thead className="bg-slate-900/60 text-slate-400">
            <tr>
              <th className="py-2.5 px-3">Nama Aset</th>
              <th className="py-2.5 px-3">Kategori</th>
              <th className="py-2.5 px-3">Tgl Beli</th>
              <th className="py-2.5 px-3 text-right">Harga Beli</th>
              <th className="py-2.5 px-3 text-center">Umur</th>
              <th className="py-2.5 px-3 text-right">Peny./Bln</th>
              <th className="py-2.5 px-3 text-right">Akumulasi</th>
              <th className="py-2.5 px-3 text-right">Nilai Buku</th>
              <th className="py-2.5 px-3 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="py-6 text-center text-slate-500">Memuat…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-500">Belum ada aset. Klik "Tambah Aset".</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className={`border-t border-slate-800/60 ${a.status === "dilepas" ? "opacity-60" : ""}`} data-testid={`aset-row-${a.id}`}>
                <td className="py-2.5 px-3 text-slate-200 font-semibold">{a.nama}{a.status === "dilepas" && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">DILEPAS</span>}</td>
                <td className="py-2.5 px-3 text-slate-400">{a.kategori || "-"}</td>
                <td className="py-2.5 px-3 text-slate-400 font-mono">{a.tanggal_beli}</td>
                <td className="py-2.5 px-3 text-right font-mono text-slate-200">{rp(a.harga_beli)}</td>
                <td className="py-2.5 px-3 text-center text-slate-300">{a.umur_tahun} th</td>
                <td className="py-2.5 px-3 text-right font-mono text-amber-300">{rp(a.penyusutan_per_bulan)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-rose-300">{rp(a.akumulasi_penyusutan)}</td>
                <td className="py-2.5 px-3 text-right font-mono font-bold text-cyan-300">{rp(a.nilai_buku)}</td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <button onClick={() => cetakKIB(a.id)} data-testid={`kib-aset-${a.id}`} title="Cetak Kartu Inventaris (KIB)" className="p-1.5 rounded-lg bg-slate-800 text-purple-300 hover:bg-slate-700"><Printer className="w-3.5 h-3.5" /></button>
                    {a.status !== "dilepas" && <button onClick={() => setDisposing(a)} data-testid={`lepas-aset-${a.id}`} title="Lepas / Jual Aset" className="p-1.5 rounded-lg bg-slate-800 text-orange-300 hover:bg-slate-700"><PackageX className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`edit-aset-${a.id}`} className="p-1.5 rounded-lg bg-slate-800 text-cyan-300 hover:bg-slate-700"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(a.id, a.nama)} data-testid={`delete-aset-${a.id}`} className="p-1.5 rounded-lg bg-slate-800 text-rose-300 hover:bg-slate-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <AsetForm initial={editing} akunList={akunList} onClose={() => setShowForm(false)} onSuccess={load} />}
      {disposing && <DisposeForm aset={disposing} akunList={akunList} onClose={() => setDisposing(null)} onSuccess={() => { setNotif("Aset berhasil dilepas."); load(); setTimeout(() => setNotif(""), 5000); }} />}
    </div>
  );
};

export default MasterAsetPage;
